import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  ReactFlowProvider,
  Node,
  Edge,
  Position,
  MarkerType,
  ConnectionLineType,
  BackgroundVariant,
} from "reactflow";
import "reactflow/dist/style.css";

import {
  WorkflowCategoryData,
  WorkflowProcessData,
  WorkflowStepData,
} from "../../../types/process";
import CustomStepNode from "../../../components/process/UI/CustomStepNode";
import ProcessNode from "../../../components/process/UI/ProcessNode";

interface WorkflowCanvasViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  category: WorkflowCategoryData | null;
  onStepNodeClick: (
    step: WorkflowStepData,
    categoryId: string,
    processId: string,
  ) => void;
}

const nodeTypes = {
  customStep: CustomStepNode,
  process: ProcessNode,
};

const PROCESS_NODE_WIDTH = 220;
const PROCESS_NODE_HEIGHT = 70;
const STEP_NODE_WIDTH = 256;
const STEP_NODE_HEIGHT = 120;
const HORIZONTAL_SPACING = 90;
const PROCESS_VERTICAL_SPACING = 80;

const workflowToFlowData = (
  category: WorkflowCategoryData,
  onStepClickCallback: (
    step: WorkflowStepData,
    categoryId: string,
    processId: string,
  ) => void,
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  let currentY = 50;

  category.processes.forEach((process: WorkflowProcessData) => {
    const processNodeId = `process-${process.id}`;
    nodes.push({
      id: processNodeId,
      type: "process",
      data: { label: process.name },
      position: { x: 50, y: currentY },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    });

    let currentX = 50 + PROCESS_NODE_WIDTH + HORIZONTAL_SPACING;

    process.steps.forEach((step, stepIndex) => {
      const stepNodeId = `step-${step.id}`;
      const nodeData = {
        step,
        onNodeClick: (s: WorkflowStepData) =>
          onStepClickCallback(s, category.id, process.id),
      };

      nodes.push({
        id: stepNodeId,
        type: "customStep",
        data: nodeData,
        position: {
          x: currentX,
          y: currentY + PROCESS_NODE_HEIGHT / 2 - STEP_NODE_HEIGHT / 2,
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      });

      if (stepIndex === 0) {
        edges.push({
          id: `edge-${processNodeId}-${stepNodeId}`,
          source: processNodeId,
          target: stepNodeId,
          type: ConnectionLineType.SmoothStep,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 18,
            height: 18,
            color: "#5B21B6",
          },
          style: { strokeWidth: 2.5, stroke: "#5B21B6" },
        });
      } else {
        const prevStepNodeId = `step-${process.steps[stepIndex - 1].id}`;
        edges.push({
          id: `edge-${prevStepNodeId}-${stepNodeId}`,
          source: prevStepNodeId,
          target: stepNodeId,
          type: ConnectionLineType.SmoothStep,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            width: 18,
            height: 18,
            color: "#5B21B6",
          },
          style: { strokeWidth: 2.5, stroke: "#5B21B6" },
        });
      }
      currentX += STEP_NODE_WIDTH + HORIZONTAL_SPACING;
    });

    currentY +=
      Math.max(STEP_NODE_HEIGHT, PROCESS_NODE_HEIGHT) +
      PROCESS_VERTICAL_SPACING;
  });

  return { nodes, edges };
};

const WorkflowCanvasViewModal: React.FC<WorkflowCanvasViewModalProps> = ({
  isOpen,
  onClose,
  category,
  onStepNodeClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { nodes, edges } = useMemo(() => {
    if (!category) return { nodes: [], edges: [] };
    return workflowToFlowData(category, onStepNodeClick);
  }, [category, onStepNodeClick]);

  if (!isOpen || !category) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "90vw",
          height: "90vh",
          maxWidth: "1400px",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {t("workflow_diagram")}: {category.name}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: theme.palette.grey[100],
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <ReactFlowProvider>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.15, minZoom: 0.3, maxZoom: 2 }}
              connectionLineType={ConnectionLineType.SmoothStep}
              nodesDraggable={true}
              elementsSelectable={true}
              proOptions={{ hideAttribution: true }}
            >
              <MiniMap
                nodeColor={n => {
                  if (n.type === "customStep") {
                    const status = n.data.step.status;
                    if (status === "Complete")
                      return theme.palette.success.main;
                    if (
                      status === "Incomplete" &&
                      n.data.step.requiresConfiguration
                    )
                      return theme.palette.warning.main;
                    return theme.palette.error.main;
                  }
                  if (n.type === "process") return theme.palette.primary.main;
                  return theme.palette.grey[400];
                }}
                nodeStrokeWidth={2}
                pannable
                zoomable
                style={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              />
              <Controls
                style={{
                  boxShadow: theme.shadows[3],
                }}
              />
              <Background
                gap={24}
                size={1.2}
                color={theme.palette.divider}
                variant={BackgroundVariant.Dots}
              />
            </ReactFlow>
          </ReactFlowProvider>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowCanvasViewModal;
